/* Preserve Shopify's selected variant when navigating between artwork formats. */
(() => {
  if (window.mcProductSwitcherBoot) { window.mcProductSwitcherBoot(); return; }
  const normalize = value => String(value || '').toLowerCase().replace(/×/g, 'x').replace(/[\s"″']/g, '');
  const dimensions = value => {
    const match = normalize(value).match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/);
    return match ? [Number(match[1]), Number(match[2])] : null;
  };
  const optionIndex = (options, name) => (options || []).findIndex(option => String(option.name || option).toLowerCase() === name);
  const option = (variant, index) => index < 0 ? '' : variant?.options?.[index] ?? variant?.['option' + (index + 1)] ?? '';

  // Resolve only published picker sizes; 24x32 is intentionally retired by the Liquid picker.
  function selectTarget(product, size, frame, changed = 'type', previousFrame = frame) {
    const sizeIndex = optionIndex(product.options, 'size');
    const frameIndex = optionIndex(product.options, 'frame');
    const available = (product.variants || []).filter(variant => variant.available && normalize(option(variant, sizeIndex)) !== '24x32');
    if (!available.length) return null;
    const matchingFrame = (variants, value) => variants.filter(variant => normalize(option(variant, frameIndex)) === normalize(value));
    const chooseFrame = variants => {
      if (frameIndex < 0) return variants;
      return [frame, previousFrame, 'No Frame'].map(value => matchingFrame(variants, value)).find(matches => matches.length) || matchingFrame(variants, option(variants[0], frameIndex));
    };
    // A frame click keeps the clicked frame and resolves its size; a size/type
    // click keeps the requested size where possible and resolves its frame.
    let candidates = changed === 'frame' ? chooseFrame(available) : available;
    const sizeMatch = value => candidates.filter(variant => normalize(option(variant, sizeIndex)) === normalize(value));
    candidates = sizeMatch(size).length ? sizeMatch(size) : sizeMatch('16x20').length ? sizeMatch('16x20') : sizeMatch(option(candidates[0], sizeIndex));
    return chooseFrame(candidates)[0];
  }

  let swapController;
  // Prefetched product pages (href -> text). Switching formats reuses them, so a
  // click only parses and swaps instead of waiting for a full server render.
  const pageCache = new Map();
  const warmed = new Set();
  function getPage(href) {
    const hit = pageCache.get(href);
    if (hit && Date.now() - hit.time < 300000) return hit.promise;
    const promise = fetch(href, {credentials: 'same-origin'}).then(response => {
      if (!response.ok) throw new Error('Product request failed');
      return response.text();
    });
    promise.catch(() => { if (pageCache.get(href)?.promise === promise) pageCache.delete(href); });
    pageCache.set(href, {time: Date.now(), promise});
    return promise;
  }
  // Fetch the page and its first gallery image ahead of the click.
  function warm(href) {
    getPage(href).then(text => {
      if (warmed.has(href)) return;
      warmed.add(href);
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const img = doc.querySelector('main product-info media-gallery img, main product-info .product__media img');
      if (!img) return;
      const preload = new Image();
      if (img.getAttribute('sizes')) preload.sizes = img.getAttribute('sizes');
      if (img.getAttribute('srcset')) preload.srcset = img.getAttribute('srcset');
      preload.src = img.getAttribute('src');
    }).catch(() => {});
  }
  const loadedScripts = new Set([...document.querySelectorAll('script[src]')].map(script => script.src));
  async function swapProduct(url, addHistory = true) {
    const current = [...document.querySelectorAll('main product-info')].find(info => info.closest('main').style.display !== 'none');
    if (!current) return;
    swapController?.abort();
    const controller = new AbortController();
    swapController = controller;
    current.setAttribute('aria-busy','true');
    current.beginPurchaseChange();
    try {
      const responseText = await getPage(url.href);
      if (controller !== swapController || controller.signal.aborted || !current.isConnected) return;
      const html = new DOMParser().parseFromString(responseText,'text/html');
      const next = html.querySelector('main');
      const nextInfo = next?.querySelector('product-info');
      const variant = JSON.parse(nextInfo?.querySelector('[data-selected-variant]')?.textContent || 'null');
      if (!next || !variant || (url.searchParams.has('variant') && String(variant.id) !== url.searchParams.get('variant'))) throw new Error('Target variant missing');
      // Reuse Dawn's section transition, retaining the URL selected by 4b.
      // Existing external scripts are already initialized; do not redeclare them.
      document.querySelectorAll('script[src]').forEach(script => loadedScripts.add(script.src));
      next.querySelectorAll('script[src]').forEach(script => {
        if (loadedScripts.has(script.src)) script.remove();
        else loadedScripts.add(script.src);
      });
      const oldGuide = current.querySelector('[data-mcsg]');
      oldGuide?._mcsgClose?.();
      current.abortController?.abort();
      current.productModal?.remove();
      // The campaign banner stays in the header, outside the replaced main.
      const data = html.getElementById('mc-product-variants');
      if(data && document.getElementById('mc-product-variants')) document.getElementById('mc-product-variants').textContent=data.textContent;
      if(addHistory) history.pushState({},'',url.href);
      document.title=html.title;
      HTMLUpdateUtility.viewTransition(current.closest('main'),next,current.preProcessHtmlCallbacks,current.postProcessHtmlCallbacks);
      requestAnimationFrame(()=>document.querySelector('[data-mc-product-switcher]')?.scrollIntoView({block:'center'}));
      return true;
    } catch (error) {
      if (error.name !== 'AbortError' && controller === swapController && current.isConnected) { current.failPurchaseChange(); throw error; }
    } finally {
      if (controller === swapController) current.removeAttribute('aria-busy');
    }
  }
  window.addEventListener('popstate',()=>{
    const url=new URL(location.href);
    if(url.pathname.startsWith('/products/') && document.querySelector('product-info')?.dataset.url!==url.pathname){
      swapProduct(url,false).catch(()=>location.reload());
    }
  });

  function init(root) {
    if (root._mcSwitcher) return;
    const lifecycle = new AbortController();
    const products = JSON.parse(root.querySelector('[data-mc-switcher-products]').textContent);
    const sourceOptions = JSON.parse(root.dataset.sourceOptions);
    const notice = root.querySelector('[data-mc-size-notice]');
    const info = root.closest('product-info') || document;
    const picker = () => [...info.querySelectorAll('variant-selects')].find(element => getComputedStyle(element).display !== 'none');
    const selected = () => JSON.parse(picker()?.querySelector('[data-selected-variant]')?.textContent || 'null');
    const say = text => { if (notice.textContent !== text) notice.textContent = text; notice.hidden = !text; };
    let noticeSet = false;
    const saveData = navigator.connection?.saveData;
    let prefetchTimer;
    const prefetchLink = link => {
      if (!link || link.classList.contains('is-active')) return;
      if (update(link)?.target) warm(link.href);
    };
    const prefetchAll = () => root.querySelectorAll('[data-product-handle]').forEach(prefetchLink);
    // Prefetch the other formats once the page is idle and again after a size change.
    const schedulePrefetch = (delay = 1200) => {
      if (saveData) return;
      clearTimeout(prefetchTimer);
      prefetchTimer = setTimeout(() => (window.requestIdleCallback || (cb => cb()))(prefetchAll, {timeout: 2000}), delay);
    };
    function update(link) {
      const source = selected();
      const product = products.find(item => item.handle === link.dataset.productHandle);
      if (!source || !product) return null;
      const size = option(source, optionIndex(sourceOptions, 'size'));
      const frame = option(source, optionIndex(sourceOptions, 'frame'));
      const target = selectTarget(product, size, frame);
      if (!target) return {error:`${normalize(size)} isn't available as ${link.dataset.productLabel}, and this product currently has no available combination.`};
      const url = new URL(link.href);
      url.searchParams.set('variant', target.id);
      url.searchParams.delete('mc_size_from');
      url.searchParams.delete('mc_frame_from');
      const targetSize = option(target, optionIndex(product.options, 'size'));
      if (normalize(targetSize) !== normalize(size)) url.searchParams.set('mc_size_from', normalize(size));
      const targetFrame = option(target, optionIndex(product.options, 'frame'));
      if (normalize(targetFrame || 'No Frame') !== normalize(frame || 'No Frame')) url.searchParams.set('mc_frame_from', frame || 'No Frame');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      else if (window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
      if (link.href !== url.href) link.href = url.href;
      return {target};
    }
    function refresh() {
      const variant = selected();
      if (!variant) return;
      root.querySelectorAll('[data-product-handle]').forEach(update);
      if (document.readyState === 'complete') schedulePrefetch();
      if (!noticeSet) {
        noticeSet = true;
        const from = new URL(location.href).searchParams.get('mc_size_from');
        const size = normalize(option(variant, optionIndex(sourceOptions, 'size')));
        const label = root.querySelector('.is-active')?.dataset.productLabel;
        const fromFrame = new URL(location.href).searchParams.get('mc_frame_from');
        const frame = option(variant, optionIndex(sourceOptions, 'frame')) || 'No Frame';
        if (dimensions(from) && normalize(from) !== size && label) say(`${normalize(from)} isn't available as ${label} – showing ${size}`);
        else if (fromFrame && normalize(fromFrame) !== normalize(frame)) say(`${fromFrame} isn't available as ${label} – showing ${frame}`);
      }
    }
    function resolveSelection(input) {
      const activePicker = picker();
      if (!activePicker?.contains(input)) return null;
      swapController?.abort();
      const position = Number(input.closest('[data-mc-option-position]')?.dataset.mcOptionPosition) - 1;
      const changed = String(sourceOptions[position] || '').toLowerCase();
      if (!['size', 'frame'].includes(changed)) return null;
      const source = selected();
      const product = products.find(item => item.handle === root.querySelector('.is-active')?.dataset.productHandle);
      if (!source || !product) return null;
      const priorSize = option(source, optionIndex(sourceOptions, 'size'));
      const priorFrame = option(source, optionIndex(sourceOptions, 'frame'));
      const size = changed === 'size' ? input.value : priorSize;
      const frame = changed === 'frame' ? input.value : priorFrame;
      const target = selectTarget(product, size, frame, changed, priorFrame);
      say('');
      if (!target) return null;
      const actualSize = option(target, optionIndex(product.options, 'size'));
      const actualFrame = option(target, optionIndex(product.options, 'frame')) || 'No Frame';
      let message = '';
      if (normalize(size) !== normalize(actualSize)) message = `${normalize(size)} isn't available with ${actualFrame} – showing ${normalize(actualSize)}`;
      else if (normalize(frame || 'No Frame') !== normalize(actualFrame)) message = `${frame || 'No Frame'} isn't available in ${normalize(actualSize)} – showing ${actualFrame}`;
      return { target, complete: () => say(message) };
    }
    // Clear the last explanation even when the next click selects the same value.
    info.addEventListener('click', event => {
      if (event.target.closest('variant-selects label, variant-selects input, [data-room-size], [data-product-handle]')) say('');
    }, { capture: true, signal: lifecycle.signal });
    root.addEventListener('click', event => {
      const link = event.target.closest('[data-product-handle]');
      if (!link) return;
      const result = update(link);
      if (!result || result.error) { event.preventDefault(); say(result?.error || 'Please wait for the selected size to finish loading.'); return; }
      if(event.button!==0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // The selected type already represents this buyable variant. Keep it in
      // place, clear the notice, and cancel a pending switch to another type.
      if (!info.purchasePending && !info.purchaseFailed && link.classList.contains('is-active') && result.target.id === selected()?.id) {
        swapController?.abort();
        info.removeAttribute('aria-busy');
        return;
      }
      // Show the choice immediately; the swap itself follows from the prefetched page.
      const previous = root.querySelector('[data-product-handle].is-active');
      const token = (root._mcSwitchToken || 0) + 1;
      root._mcSwitchToken = token;
      const setActive = active => root.querySelectorAll('[data-product-handle]').forEach(item => {
        item.classList.toggle('is-active', item === active);
        item.classList.remove('is-pending');
      });
      setActive(link);
      link.classList.add('is-pending');
      root.classList.add('is-switching');
      const finish = ok => {
        if (token !== root._mcSwitchToken || !root.isConnected) return;
        root.classList.remove('is-switching');
        link.classList.remove('is-pending');
        if (!ok) setActive(previous);
      };
      swapProduct(new URL(link.href)).then(finish, () => { finish(false); say('This product could not be loaded. Please try again.'); });
    }, { signal: lifecycle.signal });
    const intent = event => prefetchLink(event.target.closest?.('[data-product-handle]'));
    root.addEventListener('pointerover', intent, { passive: true, signal: lifecycle.signal });
    root.addEventListener('touchstart', intent, { passive: true, signal: lifecycle.signal });
    root.addEventListener('focusin', intent, { signal: lifecycle.signal });
    if (document.readyState !== 'complete') window.addEventListener('load', () => schedulePrefetch(), { once: true, signal: lifecycle.signal });
    info.addEventListener('mc:picker-ready', refresh, { signal: lifecycle.signal });
    root._mcSwitcher = {refresh, resolveSelection, destroy: () => { clearTimeout(prefetchTimer); lifecycle.abort(); }};
    refresh();
  }
  window.mcProductSwitcherBoot = () => document.querySelectorAll('[data-mc-product-switcher]').forEach(root => root._mcSwitcher?.refresh());
  customElements.define('mc-product-switcher', class extends HTMLElement {
    connectedCallback() { init(this); }
    disconnectedCallback() { this._mcSwitcher?.destroy(); delete this._mcSwitcher; }
  });
})();
